import React from 'react';
import { Link as LinkIcon } from 'lucide-react';
import { EVENTS } from '../constants/navigation';
import { UserService } from '../services/api';
import { toast } from 'sonner';

interface FormattedTextProps {
  text: string;
  className?: string;
  onMentionClick?: (username: string) => void;
}

/**
 * FormattedText Component
 *
 * Renders text with:
 * - @usernames highlighted & clickable
 * - Newlines (\\n) rendered as <br /> so line breaks entered by the user are preserved.
 */
export const FormattedText: React.FC<FormattedTextProps> = ({
  text,
  className = '',
  onMentionClick,
}) => {
  if (!text) return null;

  const trimmedText = text.trim();
  if (trimmedText === '[Voice Yap]' || trimmedText === '[Voice Reply]') {
    return null;
  }

  // Regex to find mentions (supporting alphanumeric, dots, and underscores) AND URLs
  const combinedRegex = /(@[\w\.]+|https?:\/\/[^\s]+)/g;
  const mentionRegex = /^@[\w\.]+$/;
  const urlRegex = /^https?:\/\/[^\s]+$/;

  // Split text while keeping match delimiters
  const parts = text.split(combinedRegex);

  const handleMentionClick = async (e: React.MouseEvent, mention: string) => {
    e.stopPropagation();

    const username = mention.startsWith('@') ? mention.substring(1) : mention;

    try {
      const user = await UserService.fetchUserByUsername(username);

      if (!user) {
        toast.error(`User ${username?.startsWith('@') ? username : `@${username}`} not found`, {
          description: 'This account may have been deleted or the username is incorrect.',
        });
        return;
      }

      if (onMentionClick) {
        onMentionClick(username);
      } else {
        window.dispatchEvent(
          new CustomEvent(EVENTS.NAVIGATE_TO_FRIEND_PROFILE, {
            detail: user,
          })
        );
      }
    } catch (error) {
      console.error('Error checking mention existence:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  /**
   * Renders a plain text segment, honouring newlines.
   * Each \n becomes a <br /> so user-entered line breaks are preserved.
   */
  const renderTextSegment = (segment: string, segmentIndex: number) => {
    const lines = segment.split('\n');
    return (
      <React.Fragment key={segmentIndex}>
        {lines.map((line, lineIndex) => (
          <React.Fragment key={lineIndex}>
            {line}
            {/* Insert <br /> after every line except the last */}
            {lineIndex < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

  return (
    <span className={className}>
      {parts.map((part, i) => {
        if (!part) return null;
        
        if (part.match(mentionRegex)) {
          return (
            <span
              key={i}
              onClick={(e) => handleMentionClick(e, part)}
              className="text-[#4478ea] dark:text-[#4478ea] font-semibold hover:underline cursor-pointer transition-all active:scale-95 break-all"
            >
              {part}
            </span>
          );
        }
        
        if (part.match(urlRegex)) {
          // Show the domain, not the whole URL. A share link's path is usually
          // a long opaque id that tells the reader nothing and swallows the yap
          // — "instagram.com" carries everything the domain can say. The full
          // URL stays in href and title, so nothing is lost from the link
          // itself, only from what is painted.
          let domain = part;
          try {
            domain = new URL(part).hostname.replace(/^www\./, '');
          } catch {
            // Matched the regex but is not parseable. Fall back to the raw
            // text rather than rendering an empty chip.
          }
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                // Stop the yap container behind this from navigating.
                e.stopPropagation();
                // And open the link explicitly rather than relying on
                // target="_blank". iOS Capacitor's WKWebView ignores that
                // attribute outright, and a stacked parent overlay can swallow
                // the anchor's default navigation — the same reason SocialLinks
                // opens its handles this way. So on native, tapping a link in a
                // yap did nothing at all.
                //
                // No features argument: passing one, even "noopener", makes
                // browsers treat the call as a popup request and the blocker
                // kills it. Deliberately no window.location fallback either —
                // navigating the current tab would throw the user out of the
                // app, which is worse than the link not opening.
                e.preventDefault();
                try {
                  window.open(part, '_blank');
                } catch (err) {
                  console.warn('[FormattedText] window.open failed', err);
                }
              }}
              title={part}
              // verticalAlign inline: align-baseline has no rule in the
              // committed pre-compiled stylesheet, so it would silently do
              // nothing and leave the chip sitting off the text baseline.
              style={{ verticalAlign: 'text-bottom' }}
              className="inline-flex items-center gap-1 text-[#4478ea] dark:text-[#4478ea] font-semibold hover:underline"
            >
              <LinkIcon className="w-3.5 h-3.5 shrink-0" />
              {domain}
            </a>
          );
        }
        
        return renderTextSegment(part, i);
      })}
    </span>
  );
};
